import { CaseManagementModel } from './case-management.model';
import { PermissionModel } from '@models/permission.model';
import { CustomEventModel } from '@models/custom-event.model';
import { CustomObjectModels } from '@models/custom-object.model';
import { AlertV2Schedule, EventV2Document, FlowLookup } from '@models/alerts-v2.model';
import { daySettingScheduleV2 } from '@consts/alert-events.const';
import { DashboardModel } from '@models/dashboard.model';
import { PersonGroupsModel } from '@models/person-groups.model';
import { PersonModel } from '@models/person.model';
import { VehicleGroupsModel } from '@models/vehicle-groups.model';
import { VehicleModels } from '@models/vehicle.model';
import { WallV2Model } from '@models/wall-v2.model';
import type { AlertEntry } from '../../development/alerts.service';
import { Archive as ArchiveModel, ArchiveStatus } from '@models/archive.model';
import { AnalyticClasses } from '@enums/alert-events.enum';
import { UserModel } from '@models/user.model';
import type { OnRangeSelectedResult } from '@ui';
import { Edge } from '../../edge/edge.model';
import { LocationModel } from '../../locations/location.model';
import { PulsationModels } from './pulsation.model';
import { FormControl, FormGroup } from '@angular/forms';
import { Search as SearchModels } from '../../shared/search.model';
import { ZoneRegionsModels } from '@share-models';
import { DatabaseTagsModel } from '@models/database-tags.model';
import { ScheduleModel } from '@models/schedule.model';
import { CaseCategoriesModel } from '@models/case-categories.model';

// eslint-disable-next-line @typescript-eslint/no-namespace
export namespace IntelligenceModels {
  /**
   * The second argument CopilotKit actually passes to a frontend tool handler
   * (see `FrontendToolHandlerContext` in @copilotkitnext/core). Only `toolCall`
   * is modelled — it carries the id used to correlate a tool call with the
   * panel history entry its handler produces. Optional at the handler
   * signature so handlers that ignore it stay assignable.
   */
  export interface ToolHandlerContext {
    toolCall: { id: string };
  }

  export interface FrontendTool<TParams, TResult> {
    name: string;
    description: string;
    parameters: unknown;
    handler: (args: TParams, context?: ToolHandlerContext) => Promise<TResult>;
  }

  export namespace DeviceList {
    export enum DeviceType {
      Camera = 'camera',
      Core = 'core',
      AppleTv = 'apple-tv',
      Door = 'door',
      Speaker = 'speaker',
      Sensor = 'sensor',
      RemoteIo = 'remote-io',
      ExternalStorage = 'external-storage',
      AccessControl = 'access-control',
    }

    export type HealthStatus = 'online' | 'unhealthy' | 'offline' | 'unknown';

    export interface Device {
      id: string;
      type: DeviceType;
      name: string;
      locationId: string;
      edgeId?: string;
      ipAddress?: string;
      macAddress?: string;
      host?: string;
      tags: string[];
      status: HealthStatus;
      connectedDeviceCount?: number;
      orderKey?: number;
    }

    export interface Location {
      id: string;
      name: string;
      address?: string;
      parentId?: string;
      tags: string[];
      devices: Device[];
      subLocations: Location[];
      orderKey?: number;
    }

    export interface Response {
      locations: Location[];
      locationCount: number;
      deviceCount: number;
      message: string;
    }
  }

  export enum HeaderActionKind {
    ICON = 'icon',
    BUTTON_DEFAULT = 'button-default',
    BUTTON_PRIMARY = 'button-primary',
  }

  /** A single action button a result component can publish into the shared Cmd+K context bar. */
  export interface HeaderAction {
    kind?: HeaderActionKind;
    icon?: string;
    label?: string;
    tooltip: string;
    onClick: () => void;
    disabled?: () => boolean;
    loading?: () => boolean;
  }

  // ─── Device health ────────────────────────────────────────────────────────

  /**
   * Overall device health as computed server-side by the canonical rule.
   * `Unknown` only ever appears for device kinds with no health source at all;
   * cameras and Cores always resolve to Online/Unhealthy/Offline.
   * `Installing` is emitted only for Cores mid-install; cameras never carry it.
   */
  export enum HealthStatus {
    Online = 'online',
    Unhealthy = 'unhealthy',
    Offline = 'offline',
    Unknown = 'unknown',
    Installing = 'installing',
  }

  /** Mirror of the backend IntelligenceModels.DeviceHealth contract. */
  export namespace DeviceHealth {
    /** One camera or Core row of GET /v1/intelligence/device-health. */
    export interface Item {
      id: string;
      type: DeviceList.DeviceType;
      name: string;
      locationId: string;
      locationName: string;
      /** Parent Core of a camera; equals `id` on a Core row. */
      edgeId?: string;
      status: HealthStatus;
      /**
       * Applicable components in a faulty state, present only when > 0.
       *
       * The overall `status` is a composite, so a camera with a dead substream
       * can still be Online; this is what lets a collapsed row say that
       * something is wrong without fetching the breakdown.
       */
      issueCount?: number;
      /**
       * Accordion breakdown. Populated ONLY by
       * GET /v1/intelligence/device-health/:deviceId — the list response is
       * always detail-free, so the tool result stays inside the host's cap.
       */
      details?: Details;
    }

    /** Camera and Core carry different breakdowns; `type` discriminates them. */
    export type Details = CameraDetails | CoreDetails;

    /**
     * One component row of the camera breakdown. `applicable: false` is the
     * device-list dialog's `noData`: the component is not configured for this
     * camera, so the row shows a grey cross and an em dash instead of a status.
     */
    export interface ComponentState {
      status?: PulsationModels.ComponentStatus;
      applicable: boolean;
      /**
       * The resolution/codec chips `camera-health-row` renders beside the
       * component label. Only mainStream/subStream/analytics/storage carry it.
       */
      media?: StreamMedia;
    }

    /**
     * Media facts of one stream component. Width×height is shown only when
     * BOTH are present, then the video codec, then the audio codec (web order).
     */
    export interface StreamMedia {
      width?: number;
      height?: number;
      videoCodec?: string;
      audioCodec?: string;
    }

    /** Camera breakdown; `playback` is the PARENT Core's playback status. */
    export interface CameraDetails {
      type: DeviceList.DeviceType.Camera;
      mainStream: ComponentState;
      subStream: ComponentState;
      analytics: ComponentState;
      storage: ComponentState;
      smartStorage: ComponentState;
      playback: ComponentState;
      /** Always present; `Unknown` when the stats bus was unreachable. */
      trained: Trained;
      /** Epoch ms of the oldest retained footage. */
      oldestFootageDate?: number;
      /**
       * IANA name of the camera LOCATION's timezone — the zone the device-list
       * page renders `oldestFootageDate` in. Absent when the location has none.
       */
      timezone?: string;
      imageQuality?: ImageQualityState;
      /**
       * Main-stream ping statistics — the device-list dialog's "Ping (ms)" row
       * (`ui-camera-status.component.html:113-133`).
       */
      ping?: PulsationModels.PingStats;
      /**
       * The dialog's "Info" row (`ui-camera-status.component.html:151-158`).
       * `CoreOffline` wins over an errCode.
       */
      error?: ErrorInfo;
    }

    /** Which of the two "Info" row variants applies. */
    export enum ErrorInfoKind {
      /** Parent Core offline/unhealthy — the camera's own state is unknown. */
      CoreOffline = 'coreOffline',
      /** The camera's own main-stream error code. */
      Camera = 'camera',
    }

    /**
     * One "Info" row of the camera breakdown. `code` is deliberately a plain
     * number on the web side: `IssuesModel.errorCodeToTranslationKey` is
     * number-indexed and the shared web enum must not grow for this mirror.
     */
    export interface ErrorInfo {
      kind: ErrorInfoKind;
      code?: number;
    }

    /** The three proper-fitting states the device-list dialog renders. */
    export enum TrainedState {
      Trained = 'trained',
      NotTrained = 'notTrained',
      Unknown = 'unknown',
    }

    /** Proper-fitting training progress of one camera. */
    export interface Trained {
      state: TrainedState;
      /** Days to the next thumbnail goal; set only with `NotTrained` and > 0. */
      daysToGoal?: number;
    }

    /** Analytics image-quality assessment: lower is worse. */
    export enum ImageQualityState {
      Poor = 0,
      Decent = 1,
      Good = 2,
    }

    /** Core breakdown, all of it from the Core's heartbeat metadata. */
    export interface CoreDetails {
      type: DeviceList.DeviceType.Core;
      edgeStatus?: PulsationModels.ComponentStatus;
      playback?: PulsationModels.ComponentStatus;
      platform?: string;
      swVersion?: string;
      playbackProtocol?: string;
      playbackServer?: string;
      storageAuth?: boolean;
      network: NetworkInterface[];
    }

    export enum NetworkInterfaceName {
      Ethernet1 = 'Ethernet 1',
      Ethernet2 = 'Ethernet 2',
    }

    export interface NetworkInterface {
      name: NetworkInterfaceName;
      ip?: string;
      mac?: string;
      linkSpeedMbps?: number;
      cableConnected?: boolean;
    }

    /** Response of GET /v1/intelligence/device-health/:deviceId. */
    export interface DetailResponse {
      item: Item;
      message: string;
    }

    /** How a component chip of an expanded row reads: colour and icon. */
    export enum HealthChipTone {
      Ok = 'ok',
      Warning = 'warning',
      Error = 'error',
      /** Reported-but-accepted (`notRecording`) and not-applicable components. */
      Muted = 'muted',
    }

    /**
     * One component of an expanded row, rendered as a compact tinted chip.
     *
     * The tone carries the state, so `note` is filled ONLY where the colour is
     * not the whole story (a fault's wording, the days left to retraining) —
     * repeating "Online" on every healthy chip is the noise this replaces.
     */
    export interface HealthChip {
      labelKey: string;
      tone: HealthChipTone;
      /** Short in-chip suffix, already human-readable. */
      note?: string;
      /** Overrides the tone's glyph — the trained chip keeps its own icon. */
      iconClass?: string;
      /**
       * Hover detail. EVERY chip carries one: a healthy chip deliberately prints
       * no status, so the wording has to stay reachable somewhere.
       */
      tooltipKey?: string;
      /** Interpolation params of `tooltipKey`. */
      tooltipParams?: DetailParams;
      /** Already-human-readable hover detail, when there is no key to translate. */
      tooltip?: string;
      /**
       * Pre-formatted media notes (`2560x1440`, codecs) rendered as small muted
       * spans inside the chip — the dialog's resolution/codec chips.
       */
      media?: string[];
    }

    /** One metadata field of an expanded row: label above value, in a column. */
    export interface HealthMetric {
      labelKey: string;
      /** Plain, already human-readable value; nullish renders an em-dash. */
      value?: string | null;
      /** i18n key for a translated value; wins over `value` when present. */
      valueKey?: string;
      /** Epoch ms, rendered through `ui-user-settings-date`. */
      date?: number;
      /** IANA zone `date` is rendered in; falls back to the viewer's zone. */
      dateTimezone?: string;
      /** Spans the whole grid row — for values too long to sit in one column. */
      wide?: boolean;
    }

    /** Interpolation params passed to the `translate` pipe of a detail field. */
    export type DetailParams = Record<string, string | number>;

    /** Component-status wording, mirroring `camera-health-row`'s own map. */
    export const ComponentStatusLabel: Record<PulsationModels.ComponentStatus, string> = {
      [PulsationModels.ComponentStatus.Online]: 'Online',
      [PulsationModels.ComponentStatus.Offline]: 'Offline',
      [PulsationModels.ComponentStatus.Streaming]: 'Streaming',
      [PulsationModels.ComponentStatus.Stopped]: 'Stopped',
      [PulsationModels.ComponentStatus.NotAuthenticated]: 'Not Authenticated',
      [PulsationModels.ComponentStatus.StreamNotFound]: 'Stream Not Found',
      [PulsationModels.ComponentStatus.Unknown]: 'Unknown',
      [PulsationModels.ComponentStatus.Init]: 'Init',
      [PulsationModels.ComponentStatus.Connected]: 'Connected',
      [PulsationModels.ComponentStatus.Installing]: 'Installing',
      [PulsationModels.ComponentStatus.Rebooting]: 'Rebooting',
      [PulsationModels.ComponentStatus.NotConnected]: 'Not Connected',
      [PulsationModels.ComponentStatus.Exit]: 'Exit',
      [PulsationModels.ComponentStatus.NotRecording]: 'Not Recording',
      [PulsationModels.ComponentStatus.PoorImageQuality]: 'Poor Image Quality',
      [PulsationModels.ComponentStatus.BadAlertConfiguration]: 'Bad Alert Configuration',
    };

    /**
     * The "advanced" device-list filters, decided server-side from the per-device
     * component states. Cmd+K does not offer them — the MCP panel does — but they
     * ride along in the echoed filters, so the contract stays honest.
     */
    export enum AdvancedFilter {
      DevicesWithIssues = 'devicesWithIssues',
      UnhealthyAnalytic = 'unhealthyAnalytic',
      UnhealthySubstream = 'unhealthySubstream',
      UnhealthyPlayback = 'unhealthyPlayback',
      NoCustomizedModel = 'noCustomizedModel',
      NoSubstream = 'noSubstream',
    }

    /**
     * Filters applied server-side, echoed back so the UI can render chips.
     *
     * Multi-value throughout (web device-list parity: its controls are all
     * `[multiple]`). An empty array is no constraint; a prompt naming one value
     * lands in a one-element array.
     */
    export interface Filters {
      locations: string[];
      types: DeviceList.DeviceType[];
      statuses: HealthStatus[];
      /** One free-text, case-insensitive partial device-name search. */
      name: string | null;
      tagIds: string[];
      advanced: AdvancedFilter[];
    }

    /** One selectable option of a filter dropdown, off the permitted device set. */
    export interface FacetOption {
      id: string;
      name: string;
    }

    /** Dropdown sources of the interactive filter forms (MCP panel and Cmd+K). */
    export interface Facets {
      locations: FacetOption[];
      tags: FacetOption[];
    }

    /** Per-status totals of the filtered item set. */
    export interface Counts {
      online: number;
      unhealthy: number;
      offline: number;
      /** Cores mid-install; cameras never carry `Installing`. */
      installing: number;
    }

    /**
     * i18n keys of the advanced-filter options, keyed by the STRING enum above.
     * The device-list page's `AdvancedFilterValue` is a NUMERIC enum of the same
     * six ideas — a different type, so its map cannot be reused here.
     */
    export const AdvancedFilterLabelKey: Record<AdvancedFilter, string> = {
      [AdvancedFilter.DevicesWithIssues]: 'DEVICE_LIST.ADVANCED_FILTERS.DEVICES_WITH_ISSUES',
      [AdvancedFilter.UnhealthyAnalytic]: 'DEVICE_LIST.ADVANCED_FILTERS.UNHEALTHY_ANALYTIC',
      [AdvancedFilter.UnhealthySubstream]: 'DEVICE_LIST.ADVANCED_FILTERS.UNHEALTHY_SUBSTREAM',
      [AdvancedFilter.UnhealthyPlayback]: 'DEVICE_LIST.ADVANCED_FILTERS.UNHEALTHY_PLAYBACK',
      [AdvancedFilter.NoCustomizedModel]: 'DEVICE_LIST.ADVANCED_FILTERS.NO_CUSTOMIZED_MODEL',
      [AdvancedFilter.NoSubstream]: 'DEVICE_LIST.ADVANCED_FILTERS.NO_SUBSTREAM',
    };

    /**
     * One option of a filter select whose wording is translated rather than
     * served — the static type / status / advanced lists. Facet-driven controls
     * (locations, tags) use `FacetOption`, whose name comes from the backend.
     */
    export interface FilterOption<TValue extends string> {
      value: TValue;
      labelKey: string;
    }

    /**
     * The filter a chip belongs to. Its value is the `Filters` field name, so one
     * removal handler can write straight back to the matching form control.
     */
    export enum FilterKind {
      Locations = 'locations',
      Types = 'types',
      Statuses = 'statuses',
      TagIds = 'tagIds',
      Advanced = 'advanced',
      Name = 'name',
    }

    /** One applied-filter chip rendered above the list, removable in Cmd+K. */
    export interface FilterChip {
      kind: FilterKind;
      /** i18n key of the filter's own name ("Type", "Location", …). */
      labelKey: string;
      /** Raw filter value (id or enum member) — what a removal filters out. */
      value: string;
      /** Served wording (a facet name) or the raw value when nothing matched. */
      valueLabel?: string;
      /** i18n key of the wording, for the statically-labelled filters. */
      valueLabelKey?: string;
    }

    /** One settled list response of the Cmd+K refetch pipeline, with its cache key. */
    export interface FilterLoad {
      key: string;
      response: Response;
    }

    /** Reactive shape of the Cmd+K panel's filter bar. */
    export type FiltersForm = FormGroup<{
      name: FormControl<string>;
      types: FormControl<DeviceList.DeviceType[]>;
      statuses: FormControl<HealthStatus[]>;
      locations: FormControl<string[]>;
      tagIds: FormControl<string[]>;
      advanced: FormControl<AdvancedFilter[]>;
    }>;

    /** Response from GET /v1/intelligence/device-health. */
    export interface Response {
      items: Item[];
      count: number;
      totalCount: number;
      counts: Counts;
      filters: Filters;
      facets: Facets;
      message: string;
    }

    /** Params accepted by the get-device-health tool. */
    export interface ToolParams {
      location?: string;
      type?: DeviceList.DeviceType;
      status?: HealthStatus;
      statuses?: HealthStatus[];
      advanced?: AdvancedFilter[];
      name?: string;
    }
  }

  // ─── AI chat shell ────────────────────────────────────────────────────────

  export namespace Chat {
    /** One starter chip offered by the composer while the thread is still empty. */
    export interface ComposerSuggestion {
      /** i18n key for the short chip label. */
      labelKey: string;
      /** i18n key for the full prompt submitted on click. */
      promptKey: string;
    }
  }

  // ─── Locations ────────────────────────────────────────────────────────────

  export namespace Locations {
    export enum ToolName {
      PickCamera = 'pick-camera',
      PickCameras = 'pick-cameras',
    }

    export enum PanelResultType {
      PickCamera = 'pick-camera',
    }

    export interface Edge {
      edgeId: string;
      edgeName?: string;
    }

    export interface Camera {
      cameraId: string;
      cameraName?: string;
      edgeId: string;
    }

    export interface Location {
      locationId: string;
      name: string;
      address?: string;
      city?: string;
      state?: string;
      timezone?: string;
      edgeCount: number;
      cameraCount: number;
      edges: Edge[];
      cameras: Camera[];
    }

    export interface Response {
      count: number;
      locations: Location[];
      message: string;
    }

    export interface PickCameraToolParams {
      query?: string;
      /** Compatibility field for legacy search endpoints; smart-search does not use it. */
      totalCount?: number;
      purpose?: string;
      multi?: boolean;
    }

    export interface PickCameraResult extends PickCameraToolParams {
      panelResultType: PanelResultType.PickCamera;
    }

    export interface PickCameraSelection {
      cameraId: string;
      cameraName: string;
    }

    export interface PickCamerasSelection {
      cameras: PickCameraSelection[];
    }

    export enum PickCameraCancelReason {
      /** The user left AI chat mode, started a new chat, or closed the palette. */
      Dismissed = 'dismissed',
    }

    /**
     * Returned to the model instead of a selection when the selector was torn
     * down before the user chose a camera. Without this the tool handler would
     * await forever and the turn would never finish.
     */
    export interface PickCameraCancellation {
      cancelled: true;
      reason: PickCameraCancelReason;
    }

    /** What the `pick-camera` tool resolves with — a selection or a cancellation. */
    export type PickCameraOutcome =
      | PickCameraSelection
      | PickCameraCancellation;

    /** What the `pick-cameras` tool resolves with — selections or a cancellation. */
    export type PickCamerasOutcome =
      | PickCamerasSelection
      | PickCameraCancellation;
  }

  // ─── Camera provisioning ──────────────────────────────────────────────────

  export namespace CameraProvisioning {
    export enum ToolName {
      AddCamera = 'add-camera',
    }

    export enum PanelResultType {
      AddCamera = 'add-camera',
    }

    export enum CancelReason {
      Dismissed = 'dismissed',
    }

    export enum NameResolutionKind {
      None = 'none',
      Unique = 'unique',
      Ambiguous = 'ambiguous',
    }

    export enum GroundingResultKind {
      Ready = 'ready',
      Conflict = 'conflict',
    }

    export interface AddCameraToolParams {
      locationName?: string;
      coreName?: string;
    }

    /** Resolved IDs and the opaque invocation ID consumed by the panel overlay. */
    export interface AddCameraRequest {
      requestId: string;
      locationId?: string;
      edgeId?: string;
    }

    export interface LocationNameResolution {
      kind: NameResolutionKind;
      location?: LocationModel.LocationItem;
    }

    export interface CoreNameResolution {
      kind: NameResolutionKind;
      edge?: Edge.EdgeItem;
    }

    export interface AddCameraGroundingResult {
      kind: GroundingResultKind;
      request?: AddCameraRequest;
    }

    export interface AddCameraCreated {
      created: true;
      locationName: string;
    }

    export interface AddCameraCancelled {
      cancelled: true;
      reason: CancelReason;
    }

    export interface AddCameraConflict {
      conflict: true;
    }

    export type AddCameraOutcome =
      | AddCameraCreated
      | AddCameraCancelled
      | AddCameraConflict;

    export interface AddCameraFinishedEvent {
      requestId: string;
      outcome: AddCameraCreated;
    }

    export interface AddCameraCancelledEvent {
      requestId: string;
      outcome: AddCameraCancelled;
    }

    /** Resolve a human-provided location name without reading application state. */
    export function resolveLocationName(
      locations: LocationModel.LocationItem[],
      name: string | undefined,
    ): LocationNameResolution {
      const matches = findNameMatches(locations, name, (location) => location.name);
      return matches.length === 1
        ? { kind: NameResolutionKind.Unique, location: matches[0] }
        : { kind: matches.length ? NameResolutionKind.Ambiguous : NameResolutionKind.None };
    }

    /** Resolve a human-provided Core name without reading application state. */
    export function resolveCoreName(
      edges: Edge.EdgeItem[],
      name: string | undefined,
    ): CoreNameResolution {
      const matches = findNameMatches(edges, name, (edge) => edge.name);
      return matches.length === 1
        ? { kind: NameResolutionKind.Unique, edge: matches[0] }
        : { kind: matches.length ? NameResolutionKind.Ambiguous : NameResolutionKind.None };
    }

    /**
     * Ground names once before opening the wizard. A Core wins over an unresolved
     * location, but two unique values that disagree must be clarified by the model.
     */
    export function resolveAddCameraRequest(
      params: AddCameraToolParams,
      locations: LocationModel.LocationItem[],
      edges: Edge.EdgeItem[],
      requestId: string,
    ): AddCameraGroundingResult {
      const locationResolution = resolveLocationName(locations, params.locationName);
      const coreResolution = resolveCoreName(edges, params.coreName);
      const locationId = locationResolution.location?._id;
      const edgeId = coreResolution.edge?.edgeId;
      const edgeLocationId = coreResolution.edge?.locationId;

      if (
        locationResolution.kind === NameResolutionKind.Unique &&
        coreResolution.kind === NameResolutionKind.Unique &&
        locationId &&
        edgeLocationId &&
        locationId !== edgeLocationId
      ) {
        return { kind: GroundingResultKind.Conflict };
      }

      return {
        kind: GroundingResultKind.Ready,
        request: {
          requestId,
          locationId: edgeLocationId ?? locationId,
          edgeId,
        },
      };
    }

    function findNameMatches<T>(
      items: T[],
      requestedName: string | undefined,
      getName: (item: T) => string | undefined,
    ): T[] {
      const normalizedName = requestedName?.trim().toLocaleLowerCase();
      if (!normalizedName) return [];
      const exactMatches = items.filter(
        (item) => getName(item)?.trim().toLocaleLowerCase() === normalizedName,
      );
      if (exactMatches.length) return exactMatches;
      return items.filter((item) =>
        getName(item)?.trim().toLocaleLowerCase().includes(normalizedName),
      );
    }
  }

  // ─── Core provisioning ────────────────────────────────────────────────────

  export namespace CoreProvisioning {
    export enum ToolName {
      AddCore = 'add-core',
    }

    export enum PanelResultType {
      AddCore = 'add-core',
    }

    export interface AddCoreToolParams {
      locationName?: string;
      coreId?: string;
    }

    export interface AddCoreOpened {
      opened: true;
      locationName: string;
    }

    export interface AddCoreRequest {
      locationId: string;
      coreId?: string;
    }

    export interface AddCoreCancelled {
      cancelled: true;
    }

    export type AddCoreOutcome = AddCoreOpened | AddCoreCancelled;

    /**
     * A Core must be registered against one known Location. The existing Core
     * creation screen owns the remaining inputs and provisioning workflow.
     */
    export function resolveAddCoreLocation(
      locationName: string | undefined,
      locations: LocationModel.LocationItem[],
    ): LocationModel.LocationItem | null {
      const resolution = CameraProvisioning.resolveLocationName(
        locations,
        locationName,
      );
      return resolution.kind === CameraProvisioning.NameResolutionKind.Unique
        ? resolution.location ?? null
        : null;
    }
  }

  // ─── Persons ──────────────────────────────────────────────────────────────

  export namespace Persons {
    export type Person = PersonModel.Person;

    export interface Response {
      count: number;
      persons: Person[];
      message: string;
    }
  }

  // ─── Person groups ────────────────────────────────────────────────────────

  export namespace PersonGroups {
    /** Response from GET /v1/intelligence/person-groups. */
    export interface Response {
      groups: PersonGroupsModel.PersonGroupListDocument[];
      count: number;
      message: string;
    }

    /** Response from GET /v1/intelligence/person-groups/:id/persons. */
    export interface MembersResponse {
      groupId: string;
      groupName: string;
      persons: Persons.Person[];
      /** Members returned in this response (after limit). */
      count: number;
      /** Total members in the group (before limit). */
      totalCount: number;
      message: string;
    }
  }

  // ─── Dashboards ───────────────────────────────────────────────────────────

  export namespace Dashboards {
    /** Lean list row for the get-dashboards tool + GET /v1/intelligence/dashboards. */
    export interface ListItem {
      id: number;
      name: string;
      updatedAt: number;
    }

    /** Response from GET /v1/intelligence/dashboards. */
    export interface Response {
      dashboards: ListItem[];
      count: number;
      message: string;
    }

    /** Widget descriptor stored on a dashboard-layout tile — the production web shape. */
    export type WidgetInfo = DashboardModel.WidgetInfo;

    /** Saved dashboard filters — the production web shape (the backend `filters` column). */
    export type Filters = DashboardModel.Filters;

    /**
     * One stored dashboard-layout tile — schema-compatible with what the web's
     * angular-gridster2 dashboard writes into the `layout` column (the item key
     * set is exactly {x, y, cols, rows, widgetInfo}).
     */
    export interface LayoutItem {
      x: number;
      y: number;
      cols: number;
      rows: number;
      widgetInfo: WidgetInfo;
    }

    /** Full dashboard for the Cmd+K detail view: the list row plus the stored layout. */
    export interface Detail extends ListItem {
      layout: LayoutItem[];
      filters?: Filters;
    }

    /** Response from GET /v1/intelligence/dashboards/:id. */
    export interface DetailResponse {
      dashboard: Detail;
      message: string;
    }
  }

  // ─── Vehicles ─────────────────────────────────────────────────────────────

  export namespace Vehicles {
    export type Vehicle = VehicleModels.Vehicle;

    /** Response from GET /v1/intelligence/vehicles. */
    export interface Response {
      count: number;
      vehicles: Vehicle[];
      message: string;
    }
  }

  // ─── Vehicle groups ───────────────────────────────────────────────────────

  export namespace VehicleGroups {
    /** Response from GET /v1/intelligence/vehicle-groups. */
    export interface Response {
      groups: VehicleGroupsModel.VehicleGroupListDocument[];
      count: number;
      message: string;
    }

    /** Response from GET /v1/intelligence/vehicle-groups/:id/vehicles. */
    export interface MembersResponse {
      groupId: string;
      groupName: string;
      vehicles: Vehicles.Vehicle[];
      /** Vehicles returned in this response (after limit). */
      count: number;
      /** Total vehicles in the group (before limit). */
      totalCount: number;
      message: string;
    }
  }

  // ─── Variables ────────────────────────────────────────────────────────────

  export namespace Variables {
    export interface Variable {
      name: string;
      type: string;
      value: unknown;
    }

    export interface Response {
      count: number;
      variables: Variable[];
      message: string;
    }

    export interface DatasetRowActionEvent {
      actionId: string;
      row: Variable;
    }

    export enum RowActionId {
      Edit = 'edit',
      Delete = 'delete',
    }
  }

  // ─── Zone regions ────────────────────────────────────────────────────────

  export namespace ZoneRegions {
    export interface ListResponse {
      view: 'list';
      count: number;
      query: string;
      zoneRegions: ZoneRegionsModels.ZoneRegionDocument[];
      cameraCounts: Record<string, number>;
      /** Selected camera filters kept in panel state; never returned to the model. */
      cameraFilters?: Array<{
        cameraId: string;
        edgeId: string;
        locationId?: string;
        name: string;
      }>;
    }

    export interface DetailResponse {
      view: 'detail';
      zoneRegion: ZoneRegionsModels.ZoneRegionDocument;
      cameras: ZoneRegionsModels.ZoneRegionCameraDocument[];
      cameraNames: Record<string, string>;
    }

    export type Response = ListResponse | DetailResponse;

    export type ResolveResponse =
      | { data: DetailResponse }
      | { needsInput: true; message: string };

    export type DeleteOutcome =
      | { deleted: true; id: string; name: string }
      | { cancelled: true }
      | { conflict: true }
      | { error: true };
  }

  // ─── Users ────────────────────────────────────────────────────────────────

  export namespace Users {
    /** Which source the resolved language came from — user setting, org default, or hard default. */
    export enum LanguageSource {
      User = 'user',
      Organization = 'organization',
      Default = 'default',
    }

    /** Response of GET /v1/intelligence/users/me — the caller's own profile. */
    export interface InfoResponse {
      id: string;
      firstname: string;
      lastname: string;
      fullName: string;
      email: string;
      /** `null` rather than an empty string when the user has no phone. */
      phone: string | null;
      /** Effective language, resolved through the user → organization → default chain. */
      language: string;
      languageSource: LanguageSource;
      message: string;
    }
  }

  // ─── Tags ─────────────────────────────────────────────────────────────────

  export namespace Tags {
    /**
     * One organization database tag. Same document the Database → Tags page
     * renders, plus the `orgId` the intelligence endpoint echoes back.
     */
    export interface Tag extends DatabaseTagsModel.DatabaseTagDocument {
      orgId: string;
    }

    /** Response of GET /v1/intelligence/tags. */
    export interface ListResponse {
      tags: Tag[];
      count: number;
    }

    /** Row actions the Cmd+K tags table offers, mirroring the Database → Tags page. */
    export enum RowActionId {
      Edit = 'edit',
      Delete = 'delete',
    }

    /** `ui-dataset-table` emits the raw action id, so the panel matches it against `RowActionId`. */
    export interface DatasetRowActionEvent {
      actionId: string;
      row: Tag;
    }

    // MIRROR of backend IntelligenceModels.Tags.Create — keep in sync.
    export namespace Create {
      // MIRROR of IntelligenceModels.Tags.Create.PREPARE_CREATE_TOOL_DESCRIPTION — keep in sync.
      export const PREPARE_CREATE_TOOL_DESCRIPTION =
        'Prepare an organization tag without creating it. Call this tool IMMEDIATELY with whatever the user gave; do not call list-tags first because the name is validated here. Ask only one question at a time in the order the tool returns — name, then colour, then left-panel visibility — and present question options verbatim without filling fields yourself: the default colour is an option the user must choose. Translate a colour the user names in words into an explicit #RRGGBB value, and never invent a colour they did not ask for. When prepared, restate the full summary — name, colour and left-panel visibility — and ask the user to confirm before calling create-tag with the token.';

      // MIRROR of IntelligenceModels.Tags.Create.CREATE_TOOL_DESCRIPTION — keep in sync.
      export const CREATE_TOOL_DESCRIPTION =
        'Create an organization tag only with a preparation token from prepare-create-tag, and only after the user confirms the restated summary. Never call this tool twice for one preparation; if unsure whether it succeeded, use list-tags to verify.';

      export enum ToolName {
        Prepare = 'prepare-create-tag',
        Create = 'create-tag',
      }

      export enum ErrorCode {
        InvalidPreparation = 'INVALID_PREPARATION',
        DuplicateName = 'DUPLICATE_NAME',
        LimitReached = 'LIMIT_REACHED',
        Upstream = 'UPSTREAM',
      }

      export interface Params {
        name?: string;
        color?: string;
        isShownInLeftPanel?: boolean;
      }

      export interface PreparedSummary {
        name: string;
        color: string;
        isShownInLeftPanel: boolean;
      }

      export interface Prepared {
        preparation: string;
        summary: PreparedSummary;
      }

      export interface ExecuteParams {
        preparation: string;
      }

      export interface Error {
        code: ErrorCode;
        message: string;
        recovery: string[];
      }

      export interface PrepareResponse {
        needsInput?: unknown;
        prepared?: Prepared;
        error?: Error;
      }

      export interface ExecuteResponse {
        tagCreated?: true;
        created?: Tag;
        tags?: Tag[];
        count?: number;
        message?: string;
        assistant?: unknown;
        error?: Error;
      }

      export interface ToolResponse {
        panelUpdated?: boolean;
        tagCreated?: true;
        name?: string;
        message?: string;
        count?: number;
        assistant?: unknown;
        error?: Error;
      }
    }
  }

  // ─── Event tags ───────────────────────────────────────────────────────────

  // MIRROR of backend IntelligenceModels.EventTags — keep in sync.
  export namespace EventTags {
    /**
     * One event tag. "Event tags" is the Database → Event tags page's name for
     * the custom-event definitions, so the row is a projection of the page's own
     * `CustomEventModel.CustomEvent` rather than a parallel shape.
     */
    export type EventTag = Pick<CustomEventModel.CustomEvent, '_id' | 'name'>;

    /** Response of GET /v1/intelligence/event-tags. */
    export interface ListResponse {
      eventTags: EventTag[];
      count: number;
    }

    /** Discriminator for the create-event-tag overlay. */
    export enum PanelResultType {
      CreateEventTag = 'create-event-tag',
    }

    /**
     * Payload of the create-event-tag overlay — the form's seed values. The
     * editor mutates this `CustomEvent` in place, exactly as the page does.
     */
    export interface CreateDraft {
      customEvent: CustomEventModel.CustomEvent;
    }

    /**
     * Arguments of the create-event-tag tool. Only a name: the form itself
     * collects the video length and the custom fields, and nothing is persisted
     * until the user presses Save.
     */
    export interface CreateParams {
      name?: string;
    }

    /**
     * What the create-event-tag tool reports back to the model. `draftCreated`
     * mirrors the MCP twin's marker; `name` is null when the request named
     * nothing, so the model cannot mistake a blank form for a settled name.
     */
    export interface CreateDraftResult {
      panelUpdated: true;
      draftCreated: true;
      name: string | null;
      message: string;
    }

    /** The blank form's clip length, matching the Database → Event tags page. */
    export const DEFAULT_VIDEO_LENGTH = 10;

    /**
     * The page's own seed for a new event tag — one empty, untyped field. Shared
     * by the list panel's "+" (which seeds no name) and the create-event-tag
     * tool (which seeds the one the user asked for), so the two open literally
     * the same form.
     */
    export function blankCustomEvent(name = ''): CustomEventModel.CustomEvent {
      return {
        name,
        videoLength: DEFAULT_VIDEO_LENGTH,
        fields: [{ name: '', type: undefined }],
      };
    }

    /** Row actions offered by the create-event-tag field table. */
    export enum FieldRowActionId {
      Delete = 'delete',
    }

    /** `ui-dataset-table` emits the raw action id, so the panel matches it against `FieldRowActionId`. */
    export interface FieldRowActionEvent {
      actionId: string;
      row: CustomEventModel.CustomEventField;
    }

    /** Row actions offered by the event tags list table. */
    export enum RowActionId {
      Edit = 'edit',
      Delete = 'delete',
    }

    /** `ui-dataset-table` emits the raw action id, so the panel matches it against `RowActionId`. */
    export interface RowActionEvent {
      actionId: string;
      row: EventTag;
    }
  }

  // ─── Time schedules ───────────────────────────────────────────────────────

  // MIRROR of backend IntelligenceModels.TimeSchedules — keep in sync.
  export namespace TimeSchedules {
    /**
     * One time schedule, narrowed to what the table shows. The row is a
     * projection of the Database → Time schedule page's own
     * `ScheduleModel.ScheduleDocument` rather than a parallel shape.
     */
    export type TimeSchedule = Pick<ScheduleModel.ScheduleDocument, '_id' | 'name'>;

    /** Response of GET /v1/intelligence/time-schedule. */
    export interface ListResponse {
      schedules: TimeSchedule[];
      count: number;
    }

    /** Discriminator for the create-time-schedule overlay. */
    export enum PanelResultType {
      CreateTimeSchedule = 'create-time-schedule',
    }

    /** Row actions the Cmd+K schedules table offers, mirroring the Database page. */
    export enum RowActionId {
      Edit = 'edit',
      Delete = 'delete',
    }

    /** `ui-dataset-table` emits the raw action id, so the panel matches it against `RowActionId`. */
    export interface DatasetRowActionEvent {
      actionId: string;
      row: TimeSchedule;
    }

    /**
     * Payload of the create-time-schedule overlay — the editor's seed values.
     * The same pair the Database page holds: a name and the week grid
     * `ui-time-schedule` renders.
     *
     * `document` is what tells creating from editing: absent for a new schedule,
     * carried whole from the hydrated one when a row's Edit icon opened the
     * overlay — the update dispatch spreads it under the edited name and grid,
     * exactly as the page's own save does with `selectedSchedule`. One payload
     * shape serves both, so the overlay needs no second result type of its own.
     */
    export interface CreateDraft {
      name: string;
      schedule: AlertV2Schedule[];
      document?: ScheduleModel.ScheduleDocument;
    }

    /**
     * Seed the overlay from a hydrated document (the Edit icon's path). The grid
     * is deep-copied because `ui-time-schedule` and its drag handlers mutate the
     * rows they are given, and the fetched document must not be edited in place.
     */
    export function draftFromSchedule(document: ScheduleModel.ScheduleDocument): CreateDraft {
      return {
        document,
        name: document.name ?? '',
        schedule: document.schedule?.length
          ? document.schedule.map((day) => ({
              ...day,
              times: (day.times ?? []).map((time) => ({ ...time })),
            }))
          : blankSchedule(),
      };
    }

    /**
     * Arguments of the create-time-schedule tool. Only a name: the week grid is
     * collected by the editor itself, and nothing is persisted until the user
     * presses the header's Create schedule button.
     */
    export interface CreateParams {
      name?: string;
    }

    /**
     * What the create-time-schedule tool reports back to the model.
     * `draftCreated` marks that an editor was opened rather than a schedule
     * saved; `name` is null when the request named nothing, so the model cannot
     * mistake a blank form for a settled name.
     */
    export interface CreateDraftResult {
      panelUpdated: true;
      draftCreated: true;
      name: string | null;
      message: string;
    }

    /**
     * The page's own seed for a new schedule — every day enabled, midnight to
     * midnight. Deep-copied because `ui-time-schedule` and its drag handlers
     * mutate the rows they are given, and `daySettingScheduleV2` is a shared
     * module-level constant.
     */
    export function blankSchedule(): AlertV2Schedule[] {
      return daySettingScheduleV2.map((day) => ({
        ...day,
        times: (day.times ?? []).map((time) => ({ ...time })),
      }));
    }
  }

  // ─── Custom objects ───────────────────────────────────────────────────────

  /**
   * Database → Custom objects, read-only. Both collections the page renders come
   * back in one response: appearance-trained standard objects and state-based
   * ones. The rows are the page's own models, not a parallel shape, so the same
   * `ui-custom-object-state-card` renders them on both surfaces.
   */
  export namespace CustomObjects {
    export type CustomObject = CustomObjectModels.CustomObject;
    export type CustomObjectState = CustomObjectModels.StateCustomObject;

    /** Response of GET /v1/intelligence/custom-objects. */
    export interface ListResponse {
      customObjects: CustomObject[];
      customObjectStates: CustomObjectState[];
      /** Both collections together — what the panel's card grid renders. */
      count: number;
      message: string;
    }

    /**
     * One card in the panel's grid. The two collections are keyed apart by type
     * prefix because a standard object and a state-based object can share a
     * numeric id.
     */
    export interface ListItem {
      key: string;
      item: CustomObject | CustomObjectState;
    }

    /** What the `list-custom-objects` handler reports back to the model. */
    export interface ListToolResponse {
      panelUpdated: true;
      count: number;
      message: string;
    }

    /** Arguments of the `create-custom-object` tool. */
    export interface CreateParams {
      /**
       * The object the user described, verbatim — it seeds the creation wizard's
       * description field. Absent when the user asked to create without saying what.
       */
      prompt?: string;
    }

    /**
     * A pending "open the creation wizard" request, consumed once by
     * `custom-objects-result`. The id is what makes a second create call re-fire
     * the panel's effect even when the prompt is unchanged.
     */
    export interface CreateRequest {
      id: number;
      prompt?: string;
    }

    /** What the `create-custom-object` handler reports back to the model. */
    export interface CreateToolResponse {
      panelUpdated: true;
      message: string;
    }
  }

  // ─── Organizations ────────────────────────────────────────────────────────

  export namespace Organizations {
    /** Response of GET /v1/intelligence/organizations — the caller's active org. */
    export interface InfoResponse {
      orgId: string;
      orgName: string;
    }

    /** One organization available to the authenticated user. */
    export interface UserOrganization {
      id: string;
      name: string;
    }

    /** Response of GET /v1/intelligence/organizations/user-organisations. */
    export type UserOrganizationsResponse = UserOrganization[];

    /** Full view payload rendered by the Cmd+K user-organizations result panel. */
    export interface UserOrganizationsViewResponse {
      organizations: UserOrganization[];
      count: number;
      /** The row matching the caller's active orgId; null when none matches. */
      activeOrganization: UserOrganization | null;
    }

    /** One organization permission group, reduced to what the members panel renders. */
    export interface PermissionGroup {
      id: string;
      name: string;
    }

    export interface Member {
      id: string;
      email: string;
      firstname: string;
      lastname: string;
      /** Role strategy (`roleBehavior`); the backend already sends it — used by the edit modal. */
      role?: UserModel.RolePermissionBehavior;
      /** Permission groups the member belongs to; ids index into `MembersResponse.permissionGroups`. */
      groupIds: string[];
      /** Unix seconds; absent when the member has never logged in. */
      lastLogin?: number;
    }

    /**
     * Response from GET /v1/intelligence/organizations/members.
     * All filtering is server-side; the frontend uses this shape as-is.
     */
    export interface MembersResponse {
      members: Member[];
      count: number;
      /** Inclusion filter (OR logic, comma-separated), null when showing all. */
      filter: string | null;
      /** Exclusion filter (AND NOT logic, comma-separated), null when no exclusion. */
      exclude: string | null;
      /** Ready-made summary sentence for the LLM tool result. */
      message: string;
      /** Every permission group in the organization — the panel's filter options. */
      permissionGroups: PermissionGroup[];
      /** Permission-group ids resolved from the prompt; preselect them in the members panel. */
      selectedPermissionGroupIds: string[];
    }
  }

  // ─── Search ───────────────────────────────────────────────────────────────

  export namespace Search {
    export enum InputField {
      Cameras = 'cameras',
      TimeRange = 'timeRange',
    }

    export interface NeedsInput {
      missing: InputField[];
      question: string;
      questions: { field: InputField; question: string; options: string[] }[];
    }

    export interface Filter {
      query: string;
      cameraIds: string[];
      startTime: number;
      endTime: number;
      size: number;
    }

    export interface ResultItem {
      idBase?: number;
      idIndex?: number;
      timestamp?: number;
      timestampDate?: string;
      localTime?: string;
      cameraId?: string;
      cameraName?: string;
      locationName?: string;
      edgeId?: string;
      thumbnailUrl?: string;
      zoomImageUrl?: string;
      trackerTypeId?: number;
      score?: number;
      trackTimeRange?: { start: number | null; end: number | null };
      searchObjects?: SearchModels.SearchObject[];
      searchEvents?: number[][];
    }

    export interface Response {
      success: boolean;
      query?: string;
      count?: number;
      more?: boolean;
      latestTs?: number | null;
      camerasSearched?: number;
      resolvedCameraNames?: string[];
      unresolvedCameraNames?: string[];
      filter?: Filter;
      needsInput?: NeedsInput;
      error?: string;
      results: ResultItem[];
    }
  }

  // ─── Alerts ───────────────────────────────────────────────────────────────

  export namespace Alerts {
    export const DEFAULT_LIMIT = 50;

    export interface ListParams {
      hoursBack?: number;
      startTime?: string;
      endTime?: string;
      cameraIds?: string[];
      cameraNames?: string[];
      size?: number;
      latestTs?: number;
      eventIds?: string[];
      tagIds?: string[];
      trackedObjects?: AnalyticClasses[];
      acknowledges?: boolean[];
      flowTypes?: FlowLookup[];
      /** Alert-template names from a prompt; JSON-encoded and resolved server-side. */
      alertTypes?: string[];
    }

    export interface Filter {
      hoursBack?: number;
      startTime?: string;
      endTime?: string;
      cameraIds?: string[];
      cameraNames?: string[];
      size?: number;
      latestTs?: number;
      resolvedStartTime?: string;
      resolvedEndTime?: string;
      eventIds?: string[];
      tagIds?: string[];
      trackedObjects?: AnalyticClasses[];
      acknowledges?: boolean[];
      flowTypes?: FlowLookup[];
    }

    export interface AcknowledgeOption {
      label: string;
      value: boolean;
    }

    /** Transient Cmd+K presentation state; never sent to backend APIs. */
    export interface UiState {
      eventIds: string[];
      cameras: Record<string, unknown>;
      trackedObjects: AnalyticClasses[];
      flowTypes: FlowLookup[];
      tagIds: string[];
      acknowledges: boolean[];
      dateRange?: OnRangeSelectedResult;
    }

    export interface Item {
      alertId?: string;
      eventName?: string;
      eventId?: number;
      timestamp?: number;
      localTime?: string;
      cameraId?: string;
      cameraName?: string;
      locationName?: string;
      locationId?: string;
      edgeId?: string;
      alertMessage?: string;
      category?: string;
      priority?: number;
      thumbnailUrl?: string | null;
      archived?: boolean;
      eventTags?: string[];
      /** Full production alert shape consumed by AlertPreviewComponent. */
      monitoringAlert?: AlertEntry;
    }

    export interface BreakdownItem {
      cameraId?: string;
      cameraName?: string;
      locationName?: string;
      eventName?: string;
      count: number;
      date?: string;
      hourStartIso?: string;
    }

    export interface Response {
      success: boolean;
      totalCount?: number;
      camerasSearched?: number;
      error?: string;
      unresolvedAlertTypes?: string[];
      results: Item[];
      filter?: Filter;
      hasMore?: boolean;
      breakdown?: {
        byCamera: BreakdownItem[];
        byEventType: BreakdownItem[];
        byDay: BreakdownItem[];
        byHour: BreakdownItem[];
      };
      topN?: {
        cameras: BreakdownItem[];
        eventTypes: BreakdownItem[];
        days: BreakdownItem[];
      };
      uiState?: UiState;
    }
  }

  // ─── Alert configurations ───────────────────────────────────────────────────

  export namespace AlertConfigurations {
    export const DEFAULT_LIMIT = 100;

    export enum SetEnabledStatus {
      Updated = 'updated',
      AlreadyInState = 'already-in-state',
      NotFound = 'not-found',
      Ambiguous = 'ambiguous',
      Failed = 'failed',
    }

    /** A single alert-configuration row — the full alert-rule document the panel renders. */
    export type Item = EventV2Document;

    /** Parameters for an alert-configurations fetch — initial page, filter re-query, pagination. */
    export interface ListParams {
      limit?: number;
      query?: string;
      /** Cursor: return configs whose `_id` is older than this ObjectId (hex). */
      lastId?: string;
      cameraIdsIncludes?: string[];
      cameraIdsExcludes?: string[];
      tagIds?: string[];
      flowTypes?: FlowLookup[];
      /**
       * Camera names as the user phrased them (partial, case-insensitive), resolved
       * server-side into camera IDs — the prompt path, since the assistant never
       * holds IDs. Sent JSON-encoded because names may contain commas.
       */
      cameraNames?: string[];
      /** Tag names as the user phrased them; resolved server-side into `tagIds`. */
      tagNames?: string[];
      /** Alert type/template names as the user phrased them; resolved into `flowTypes`. */
      alertTypes?: string[];
    }

    /** A camera name resolved from `ListParams.cameraNames`, shaped for a camera chip. */
    export interface ResolvedCamera {
      cameraId: string;
      edgeId: string;
      locationId: string;
      name: string;
    }

    /** A tag name resolved from `ListParams.tagNames`. */
    export interface ResolvedTag {
      _id: string;
      name: string;
      color?: string;
    }

    /** An alert type resolved from `ListParams.alertTypes`, carrying its catalogue label. */
    export interface ResolvedFlowType extends FlowLookup {
      label: string;
    }

    /** Names supplied in the request that matched nothing; each one emptied the result set. */
    export interface UnresolvedNames {
      cameraNames?: string[];
      tagNames?: string[];
      alertTypes?: string[];
    }

    /**
     * The filter the backend actually applied, echoed back so the panel can seed its
     * filter bar with what the assistant asked for. Everything past the first three
     * fields is present only when that filter was applied.
     */
    export interface Filter {
      limit: number;
      query: string | null;
      lastId: string | null;
      cameraIds?: string[];
      /** Display data for the resolved cameras; only present when `cameraNames` was supplied. */
      cameras?: ResolvedCamera[];
      tagIds?: string[];
      /** Display data for the resolved tags; only present when `tagNames` was supplied. */
      tags?: ResolvedTag[];
      flowTypes?: ResolvedFlowType[];
      unresolved?: UnresolvedNames;
    }

    export interface Response {
      items: Item[];
      count: number;
      filter: Filter;
      message: string;
      /** Client-side cursor-pagination flag: another page may exist. */
      hasMore?: boolean;
      /** Transient Cmd+K presentation state; never sent to backend APIs. */
      uiState?: UiState;
    }

    export interface UiState {
      query: string;
      cameras: Record<
        string,
        {
          cameraId: string;
          edgeId: string;
          locationId?: string;
          name?: string;
        }
      >;
      flowTypes: FlowLookup[];
      tagIds: string[];
      scrollOffset: number;
    }

    export interface SetEnabledParams {
      /** Deprecated compatibility selector for cached/existing clients. */
      name?: string;
      names?: string[];
      all?: true;
      enabled: boolean;
    }

    export interface SetEnabledResult {
      requestedName: string;
      status: SetEnabledStatus;
      name: string | null;
      enabled: boolean;
      matches: string[];
      message: string;
    }

    export interface SetEnabledSummary {
      total: number;
      updated: number;
      alreadyInRequestedState: number;
      notFound: number;
      ambiguous: number;
      failed: number;
    }

    /**
     * PUT /v1/intelligence/alert-configurations/status. Carries the affected
     * configurations as a list response (backend twin: `enabledChanged` marker)
     * so the preview panel re-renders them right after the state change.
     */
    export interface SetEnabledResponse extends Response {
      enabledChanged: true;
      results: SetEnabledResult[];
      summary: SetEnabledSummary;
    }

    /**
     * `set-alert-configuration-enabled` tool result. The affected `items` go to
     * the preview panel, never into the chat context — only the batch outcome is
     * returned to the assistant.
     */
    export interface SetEnabledToolResponse {
      panelUpdated?: true;
      results: SetEnabledResult[];
      summary: SetEnabledSummary;
      message: string;
    }

    export interface CreateParams {
      template?: string;
      name?: string;
    }

    export interface TemplateAvailability {
      hasProtectiveGear: boolean;
      hasDoors: boolean;
      hasShelves: boolean;
      isDeveloper: boolean;
      permissions: PermissionModel.UserPermissionsState;
      hasAccessDoors: boolean;
      violenceEnabled: boolean;
      shelvesEnabled: boolean;
      hasObjectStates: boolean;
      hasPhysicalAccessControls: boolean;
      faceRecognitionEnabled: boolean;
      enablePalestinianLicensePlate: boolean;
      disableMissingObjectEventConfiguration: boolean;
    }

    export interface TemplateMatch {
      template: FlowLookup;
      label: string;
    }

    /**
     * Overlay payload for the in-panel alert creation flow. `template` preselects
     * the flow (mirrors the /alerts-v2/create/:category/:type route); null shows
     * the "when this happens" trigger selector (create-from-scratch).
     */
    export interface CreateAlertDraft {
      template: FlowLookup | null;
      name?: string;
      /** Full event document used as the lossless edit/merge base. */
      configuration?: EventV2Document;
    }
  }

  // ─── Case Manager ──────────────────────────────────────────────────────────

  export namespace CaseManager {
    export type Case = CaseManagementModel.CaseDocument;

    export interface ListParams {
      limit?: number;
      query?: string;
      statuses?: CaseManagementModel.CaseStatus[];
      tags?: string[];
      latestTs?: number;
    }

    export interface Filter {
      limit: number;
      query: string | null;
      statuses: CaseManagementModel.CaseStatus[];
      tags: string[];
      latestTs: number | null;
    }

    export interface Response {
      cases: Case[];
      count: number;
      filter: Filter;
      message: string;
    }

    export type FeatureType =
      | 'clips'
      | 'objects'
      | 'attachments'
      | 'accessControlEvents';

    export interface FeatureItem {
      id: FeatureType;
      labelKey: string;
    }

    export interface FeatureResponse {
      caseId: string;
      caseName: string;
      feature: FeatureType;
      userPermission: CaseManagementModel.CaseUserPermission | null;
    }

    // ─── Categories ─────────────────────────────────────────────────────────
    // MIRROR of backend IntelligenceModels.CaseManager.Category / CategoriesResponse.
    // Categories are a sub-route of case-manager on both hosts, so they live in this
    // namespace rather than one of their own.

    /** One case category, as GET /v1/intelligence/case-manager/categories returns it. */
    export type Category = CaseCategoriesModel.CaseCategoryDocument;

    /** Response of GET /v1/intelligence/case-manager/categories. */
    export interface CategoriesResponse {
      categories: Category[];
      count: number;
    }

    /**
     * Row actions offered by the case categories list table, matching the
     * Database → Categories page: Edit reopens the page's own dialog on the row,
     * Delete confirms and then dispatches through the page's store.
     */
    export enum CategoryRowActionId {
      Edit = 'edit',
      Delete = 'delete',
    }

    /** `ui-dataset-table` emits the raw action id, so the panel matches it against `CategoryRowActionId`. */
    export interface CategoryRowActionEvent {
      actionId: string;
      row: CaseCategoriesModel.CategoryWithStringRetention;
    }
  }

  // ─── Archive ──────────────────────────────────────────────────────────────

  export namespace Archive {
    export const DEFAULT_LIMIT = 100;

    export namespace Create {
      // MIRROR of ArchiveModels.Create.PREPARE_CREATE_TOOL_DESCRIPTION — keep in sync.
      export const PREPARE_CREATE_TOOL_DESCRIPTION =
        "Prepare an archive or timelapse for one camera and time range without creating it. When the user references a camera, call this tool IMMEDIATELY with whatever values they gave — do NOT call get-locations first: this tool resolves the camera itself and returns needsInput.resolvedCamera plus questions for anything missing (get-locations is only for browsing cameras). If this tool returns needsInput, ask the user about each entry of needsInput.questions as its own concise question, presenting that entry's options verbatim as a short list (the user may always answer with a custom value); when the camera is missing, unmatched, or ambiguous, needsInput.question directs you to call pick-camera so the user selects one interactively — follow it. Fields the user already provided never appear in needsInput.missing — never re-ask them. Never fill a missing field yourself: the archive name (including the \"Untitled archive\" preset) must be the user's explicit answer to its question; however, if the user's request already names archive or timelapse (in any language), pass mode accordingly — that is grounding the user's words, not filling a missing field; if a field is in needsInput.missing without a question yet, collect the other answers and call this tool again to get its question. Do not use MCP elicitation. After collecting values, call prepare-create-archive again. When it returns prepared, restate the summary (system-resolved camera name/location, range, mode, name), get user confirmation, then call create-archive with preparation.";

      // MIRROR of ArchiveModels.Create.CREATE_TOOL_DESCRIPTION — keep in sync.
      export const CREATE_TOOL_DESCRIPTION =
        'Create an archive only with a preparation token from prepare-create-archive after the user confirms its summary. Never call this tool twice for the same request; if unsure whether it succeeded, call list-archives first. Report success only when the result contains created.archiveId; an error or missing archiveId is not a successful creation. Report the created archive name from the tool result.';

      export enum ToolName {
        Prepare = 'prepare-create-archive',
        Create = 'create-archive',
      }

      export enum Mode {
        Archive = 'archive',
        Timelapse = 'timelapse',
      }

      export enum ErrorCode {
        NoPermission = 'NO_PERMISSION',
        EdgeTimeout = 'EDGE_TIMEOUT',
        Upstream = 'UPSTREAM',
        InvalidPreparation = 'INVALID_PREPARATION',
      }

      export interface Params {
        mode?: Mode;
        cameraId?: string;
        cameraName?: string;
        startTime?: string | number;
        endTime?: string | number;
        name?: string;
        outputDurationMinutes?: number;
      }

      export interface ExecuteParams {
        preparation: string;
      }

      export interface PrepareResponse {
        needsInput?: unknown;
        prepared?: unknown;
      }

      /**
       * What the web `prepare-create-archive` tool hands back to the model.
       *
       * Adds two things the backend response has no reason to carry:
       * - `error`, mirroring the shape the MCP tool puts in its content on
       *   failure, so a rejected call is recoverable instead of fatal.
       * - `currentTime`/`timezone`, because the Cmd+K chat runtime — unlike
       *   claude.ai — injects no current date, so the model otherwise cannot
       *   resolve "yesterday at 15:42" into an absolute time. Supplied per
       *   call: the tool list is built once at app bootstrap, so anything
       *   baked into a description would be stale for the whole session.
       */
      export interface PrepareToolResponse extends PrepareResponse {
        error?: Error;
        currentTime?: string;
        timezone?: string;
      }

      export interface Accepted {
        accepted: true;
        sessionId?: string;
        archiveId: string;
        mode: Mode;
        cameraId: string;
        name: string;
        cameraName: string;
        start: number;
        end: number;
      }

      export interface Error {
        code: ErrorCode;
        message: string;
        recovery: string[];
      }

      export interface FollowUp {
        tool?: string;
        args?: Record<string, unknown>;
        suggest: string;
        reason?: string;
      }

      export interface AssistantBlock {
        hint: string;
        followUps: FollowUp[];
      }

      export interface ExecuteResponse {
        created?: Accepted;
        archives?: unknown[];
        filter?: unknown;
        error?: Error;
        message?: string;
        assistant?: AssistantBlock;
      }

      export interface ToolResponse extends ExecuteResponse {
        panelUpdated?: boolean;
      }
    }

    export enum ListType {
      Archive = 'archive',
      Timelapse = 'timelapse',
      Clip = 'clip',
    }

    export interface ResolvedCamera {
      cameraId: string;
      name: string;
    }

    export interface ListArchivesParams {
      limit?: number;
      query?: string;
      latestTs?: number;
      cameraName?: string;
      cameraIds?: string[];
      statuses?: ArchiveStatus[];
      types?: ListType[];
      start?: number;
      end?: number;
    }

    export interface ListArchivesToolParams extends Omit<ListArchivesParams, 'start' | 'end'> {
      startMs?: number;
      endMs?: number;
    }

    export interface FetchArchivesParams extends ListArchivesParams {
      startMs?: number;
      endMs?: number;
    }

    export enum RowAction {
      View = 'view',
      Share = 'share',
      Download = 'download',
      VideoSignature = 'video-signature',
      Retry = 'retry',
      AddToCase = 'add-to-case',
      Delete = 'delete',
    }

    export interface Filter {
      limit: number;
      query: string | null;
      latestTs: number | null;
      cameraName?: string;
      cameraIds?: string[];
      statuses?: ArchiveStatus[];
      types?: ListType[];
      start?: number;
      end?: number;
      resolvedCameras?: ResolvedCamera[];
      unresolvedCameraNames?: string[];
    }

    export interface Response {
      archives: ArchiveModel.ArchiveDocument[];
      count: number;
      filter: Filter;
      message: string;
      /** Client-side cursor-pagination flag: another page may exist. */
      hasMore?: boolean;
      /** Transient Cmd+K presentation state; never sent to backend APIs. */
      highlightedSessionId?: string;
    }
  }

  // ─── Walls ────────────────────────────────────────────────────────────────

  export namespace Walls {
    export const DEFAULT_LIMIT = 100;

    // MIRROR of backend IntelligenceModels.Walls.Create — keep in sync.
    export namespace Create {
      // MIRROR of IntelligenceModels.Walls.Create.PREPARE_CREATE_TOOL_DESCRIPTION — keep in sync.
      export const PREPARE_CREATE_TOOL_DESCRIPTION =
        'Prepare a video wall without creating it. Call this tool IMMEDIATELY with whatever the user gave; do not call discovery tools first because camera names are resolved here. Ask only one question at a time, always name before cameras, and present question options verbatim without filling fields yourself: "Untitled wall" is an option the user must choose. For cameras, call pick-cameras and pass selected IDs verbatim; if the user declines, call this tool again with noCameras true. When prepared, restate the summary and ask exactly one question: review in editor or save now. Call review-wall-draft or create-wall with the token accordingly.';

      // MIRROR of IntelligenceModels.Walls.Create.CREATE_TOOL_DESCRIPTION — keep in sync.
      export const CREATE_TOOL_DESCRIPTION =
        'Create a video wall only with a preparation token from prepare-create-wall after the user chooses save now. Never call this tool twice; if unsure, use list-walls to verify. review-wall-draft and create-wall are MUTUALLY EXCLUSIVE per request: after review-wall-draft, the user saves in the panel and you must never call create-wall for that flow.';

      // MIRROR of IntelligenceModels.Walls.Create.REVIEW_TOOL_DESCRIPTION — keep in sync.
      export const REVIEW_TOOL_DESCRIPTION =
        'Open an editable prepared video-wall draft. The wall is not saved until the user presses Save in the panel. review-wall-draft and create-wall are MUTUALLY EXCLUSIVE per request: after this tool, the user saves in the panel and you must never call create-wall for that flow.';

      export enum ToolName {
        Prepare = 'prepare-create-wall',
        Create = 'create-wall',
        Review = 'review-wall-draft',
      }

      export enum ErrorCode {
        NoPermission = 'NO_PERMISSION',
        Upstream = 'UPSTREAM',
        InvalidPreparation = 'INVALID_PREPARATION',
      }

      export interface Params {
        name?: string;
        cameraIds?: string[];
        cameraNames?: string[];
        noCameras?: boolean;
      }

      export interface PreparedDraft {
        name: string;
        cameraIds: string[];
      }

      export interface Prepared {
        preparation: string;
        summary?: unknown;
        draft: PreparedDraft;
      }

      export interface PreparationPayload {
        orgId: string;
        name: string;
        cameraIds: string[];
        exp: number;
      }

      export interface ExecuteParams {
        preparation: string;
      }

      export interface Error {
        code: ErrorCode;
        message: string;
        recovery: string[];
      }

      export interface PrepareResponse {
        needsInput?: unknown;
        prepared?: Prepared;
        error?: Error;
      }

      export interface ExecuteResponse extends Partial<CreateResponse> {
        wallCreated?: true;
        error?: Error;
        assistant?: unknown;
      }

      export interface ToolResponse extends ExecuteResponse {
        panelUpdated?: boolean;
        draftOpened?: boolean;
      }
    }

    /** A single wall row as displayed in the search-modal preview panel. */
    export interface Item {
      _id: string;
      name: string;
      /** True when the wall is private to its owner; false when shared. */
      isPrivate: boolean;
      /** Count of all tiles in the first set, matching the /wall-v2/list Tiles column. */
      tilesCount: number;
      createdAt: number | null;
    }

    export interface Filter {
      limit: number;
      query: string | null;
      latestTs: number | null;
    }

    /** Parameters for a walls fetch — initial page, filter re-query, and pagination. */
    export interface ListParams {
      limit?: number;
      query?: string;
      latestTs?: number;
      /** Wall type: false = public, true = private. Empty = both. */
      isPrivate?: boolean[];
      /** Wall tag IDs to filter by (any-of match). */
      tags?: string[];
      cameraIdsIncludes?: string[];
      cameraIdsExcludes?: string[];
    }

    export interface CreateWallParams {
      name: string;
      /** Camera IDs to place on the wall, in order. Omit for an empty wall. */
      cameraIds?: string[];
    }

    /** Parameters for opening one wall directly in the camera-detail result. */
    export interface ViewWallParams {
      name: string;
    }

    /** Frontend-only draft payload for the Cmd+K AI create-wall editor. */
    export interface CreateDraft {
      name: string;
      cameraIds?: string[];
      wall?: WallV2Model.WallMongoDocument;
    }

    export interface Response {
      walls: Item[];
      count: number;
      filter: Filter;
      message: string;
      /** Client-side cursor-pagination flag: another page may exist. */
      hasMore?: boolean;
    }

    /**
     * Response returned by the create-wall tool. Extends the list response so
     * consumers can render the refreshed wall list without a second fetch.
     */
    export interface CreateResponse extends Response {
      success: boolean;
      wallId: string;
      name: string;
      tilesCount: number;
    }

    /** Payload for the wall camera-preview overlay (drill-down from a wall row). */
    export interface DetailRequest {
      _id: string;
      name: string;
    }
  }

  // ─── Charts ───────────────────────────────────────────────────────────────

  export namespace Charts {
    /**
     * Chart templates the backend chart tools can render. Mirrors `PlotType` in
     * `backend/apps/intelligence/src/utils/chart-templates.ts`.
     */
    export enum PlotType {
      ObjectDetectionsByHour = 'object_detections_by_hour',
      ObjectDetectionsByCamera = 'object_detections_by_camera',
      ObjectGroupsByHour = 'object_groups_by_hour',
      ObjectTypesByHour = 'object_types_by_hour',
      ObjectTypesByCamera = 'object_types_by_camera',
      AlertsByHour = 'alerts_by_hour',
      AlertsByCamera = 'alerts_by_camera',
      AlertTypesDistribution = 'alert_types_distribution',
    }

    /** What a bar's raw label identifies — decides how a bar click is resolved. */
    export enum XAxisType {
      Time = 'time',
      Timestamp = 'timestamp',
      Date = 'date',
      Camera = 'camera',
      AlertType = 'alert_type',
    }

    /** Time periods the chart tools accept. */
    export enum Timeframe {
      Last24Hours = 'last_24_hours',
      SameDayLastWeek = 'same_day_last_week',
      LastWeek = 'last_week',
      LastMonth = 'last_month',
    }

    export enum ObjectType {
      Person = 'person',
      Vehicle = 'vehicle',
      Animal = 'animal',
    }

    export interface AppliedCamera {
      cameraId: string;
      edgeId: string;
      name: string;
    }

    export interface AppliedFilter {
      cameras: AppliedCamera[];
      /** Omitted when analytics used every tracker class. */
      objectTypes?: ObjectType[];
    }

    export interface BarSelection {
      labelIndex: number;
      seriesIndex: number;
    }

    export type BarSelectionPredicate = (selection: BarSelection) => boolean;

    export interface SeriesItem {
      label: string;
      values: (number | null)[];
    }

    export interface TimeRange {
      start?: number;
      end?: number;
      timezone?: string;
    }

    export interface Data {
      labels: string[];
      /** Machine identity per bar (epoch ms, camera id, alert name); `labels` is display text. */
      rawLabels?: string[];
      series: SeriesItem[];
    }

    export interface Response {
      success: boolean;
      plotType?: PlotType;
      timeframe?: Timeframe;
      xAxisType?: XAxisType;
      error?: string;
      data?: Data;
      timeRange?: TimeRange;
      appliedFilter?: AppliedFilter;
    }

    /** Chart colors resolved from the active theme's CSS variables. */
    export interface Theme {
      /** Categorical series palette, as `#rrggbb`. */
      series: string[];
      grid: string;
      text: string;
      tooltipBackground: string;
      tooltipText: string;
      tooltipBorder: string;
    }

    /** One summary-table row: the x-axis label plus one numeric cell per series. */
    export interface TableRow {
      id: string;
      label: string;
      /**
       * Series cells are keyed positionally (`s0`, `s1`, …) rather than by
       * series label — labels are server-supplied free text and would collide
       * with `id`/`label` or with each other.
       */
      [seriesKey: string]: string | number | null;
    }
  }

  // ─── Result union for history ─────────────────────────────────────────────

  export type ResultType =
    | 'organization-members'
    | 'deviceHealth'
    | 'locations'
    | 'persons'
    | 'search'
    | 'alerts'
    | 'caseManager'
    | 'caseManagerFeature'
    | 'chart';

  export interface HistoryEntry {
    type: ResultType;
    prompt: string;
    data:
      | Organizations.MembersResponse
      | DeviceHealth.Response
      | Locations.Response
      | Persons.Response
      | Search.Response
      | Alerts.Response
      | CaseManager.Response
      | CaseManager.FeatureResponse
      | Charts.Response;
  }
}
